/**
 * Who is signed in.
 *
 * The user record is kept here; the tokens live in the API client, in memory. That split is
 * deliberate — a component that needs the user's name should not be able to reach their
 * credential, and nothing in this store is worth persisting to disk.
 */
import { create } from 'zustand'

import { api, setTokens } from '@/api/client'
import type { TokenResponse, UserSummary } from '@/api/types'

type SessionState = {
  user: UserSummary | null
  status: 'unknown' | 'signed-out' | 'signed-in'
  error: string | null
  signIn: (email: string, password: string, orgSlug?: string) => Promise<void>
  signOut: () => Promise<void>
  hasRole: (...roles: string[]) => boolean
}

export const useSession = create<SessionState>((set, get) => ({
  user: null,
  status: 'unknown',
  error: null,

  async signIn(email, password, orgSlug) {
    set({ error: null })
    try {
      const body: Record<string, unknown> = { email, password }
      // Only sent when supplied: a consultant working for two client organisations needs
      // it, and everyone else should not have to know their own tenant slug.
      if (orgSlug) body['org_slug'] = orgSlug

      const response = await api.post<TokenResponse>('/auth/login', body)
      setTokens({ access: response.access_token, refresh: response.refresh_token })
      set({ user: response.user, status: 'signed-in', error: null })
    } catch (error) {
      set({
        status: 'signed-out',
        error: error instanceof Error ? error.message : 'Sign-in failed',
      })
      throw error
    }
  },

  async signOut() {
    try {
      await api.post('/auth/logout')
    } catch {
      // A failed logout call must still clear the client. Leaving someone apparently
      // signed in because the network blipped is the wrong way to fail.
    }
    setTokens(null)
    set({ user: null, status: 'signed-out', error: null })
  },

  hasRole(...roles) {
    const held = get().user?.roles ?? []
    return roles.some((role) => held.includes(role))
  },
}))
